"use client";

import { TagTypeEnum } from "@/app/types/typeConfig/chatQuestion";
import { useQueryParams } from "./useQueryParams";

/**
 * 定义操作类型
 * @enum QueryBehaviorTypeEnum
 * @description
 * 此枚举类型用于定义通过路由控制行为的类型，一个场景一个type
 * 行为控制的路由参数尽量少定义，避免过多的路由参数导致复杂性增加。
 *
 * 1 - GUIDE_MENU_CLICK - 导航菜单交互
 * 2 - HANDY_CHAT_SEND - handy发送聊天内容
 */
export enum QueryBehaviorTypeEnum {
  GUIDE_MENU_CLICK = 1,
  HANDY_CHAT_SEND = 2,
}

// 查询参数配置接口
interface QueryConfig {
  defaultValue: string | number | boolean; // 默认值
  type?: "string" | "number" | "boolean";
}

// 基础配置接口
interface CommonConfig {
  template?: React.ComponentType<any>; // 模板
  action?: Function; // 行为函数
}

// 针对不同行为类型的接口
interface GuideMenuClickConfig extends CommonConfig {
  urlParams: {
    tagType: {
      defaultValue: keyof typeof TagTypeEnum | "";
      type: "string";
    }; // 引导菜单点击id
  };
}

interface ChatSendConfig extends CommonConfig {
  urlParams: {
    promptType: QueryConfig; // 提示词类型
  };
}

// 定义查询参数的配置映射
type QueryBehaviorConfigMap = {
  [QueryBehaviorTypeEnum.GUIDE_MENU_CLICK]: GuideMenuClickConfig;
  [QueryBehaviorTypeEnum.HANDY_CHAT_SEND]: ChatSendConfig;
};

// 实现查询参数的配置
const queryBehaviorListConfig: QueryBehaviorConfigMap = {
  [QueryBehaviorTypeEnum.GUIDE_MENU_CLICK]: {
    urlParams: {
      // 引导菜单类型
      tagType: {
        defaultValue: "",
        type: "string",
      },
    },
  },
  [QueryBehaviorTypeEnum.HANDY_CHAT_SEND]: {
    urlParams: {
      // 提示词类型, 表示从handy跳转过来不同的聊天场景入口
      // 自定义prompt：promptType = 1；
      // 设备页浮球：promptType = 2；
      // 发起打印流程中：promptType = 3；
      // 帮助中心搜索：promptType = 4；
      // 绑定打印机：promptType = 5；
      promptType: {
        defaultValue: "",
        type: "number",
      },
    },
  },
};

/**
 * 根据行为类型获取查询参数
 * @param type 行为类型
 * @returns 查询参数对象，包含behaviorType和对应配置中的参数
 */
export function useQueryChatBehavior(type: QueryBehaviorTypeEnum): {
  queryParams: Record<string, string | number | boolean> | null;
} {
  // 获取对应类型的配置
  const config = queryBehaviorListConfig[type] || {};

  // 构建查询参数配置
  const queryParams = useQueryParams({
    behaviorType: {
      defaultValue: "",
      type: "number",
    },
    ...config.urlParams,
  });

  const { behaviorType } = queryParams;
  // 如果从路由拿到的行为类型和注册的行为类型匹配
  if (behaviorType === type) {
    // 且有action，则执行对应的action
    if (config.action) {
      config.action(queryParams);
    }
    return {
      queryParams,
    };
  }
  return {
    queryParams: null,
  };
}
